import {ClockIcon} from '@heroicons/react/24/outline';
import classNames from 'classnames';
import React, {ReactElement, RefObject, useEffect, useState} from 'react';

import {MoneyIcon, ProductionIcon} from '../../../resources/Icons';
import Row from '../../common/Row';
import {useGameStatus} from '../hooks';
import AnimatedNumberPill from './buttons/AnimatedNumberPill';
import {SimplePill} from './SimplePill';

type Props = {
  triggerElement: RefObject<HTMLElement>;
};

const FixedBalance = ({triggerElement}: Props): ReactElement => {
  const gameStatus = useGameStatus();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        setVisible(!entries[0].isIntersecting);
      },
      {rootMargin: '-60px 0px 0px 0px'}
    );
    const currentElement = triggerElement.current;

    if (currentElement) {
      setTimeout(() => observer.observe(currentElement), 1000);
    } else {
      setVisible(false);
    }

    return () => {
      if (currentElement) observer.unobserve(currentElement);
    };
  }, [triggerElement]);

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        left: 0,
        paddingTop: '2.08%',
        paddingRight: '2.08%',
        paddingBottom: 'calc(2.08% + 4px)',
        paddingLeft: '2.08%',
      }}
      className={classNames('z-[9] backdrop-blur-lg', !visible && 'hidden')}
    >
      <SimplePill className={'!w-auto !px-xs'}>
        <Row className={'self-stretch items-center gap-xs overflow-x-auto overflow-y-hidden'}>
          <Row className={'grow items-center gap-2 whitespace-nowrap text-base'}>
            <ClockIcon className="h-7 w-7" />
            <span>{gameStatus.currentYear + 1}</span>
          </Row>
          <AnimatedNumberPill
            id="totalBalance"
            icon={<MoneyIcon className="h-[45px] w-[45px]" />}
            numberPrefix={gameStatus.money < 0 ? '-£' : '£'}
            number={gameStatus.money}
            danger={gameStatus.money < 0}
            normalizeValue
            hasComma
            compact
          />
          <AnimatedNumberPill
            id="production"
            icon={<ProductionIcon />}
            number={+gameStatus.totalProduction.toFixed(2)}
            numberSuffix="t"
            hasComma={false}
            compact
          />
        </Row>
      </SimplePill>
    </div>
  );
};

export default FixedBalance;
